import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin, Sparkles } from "lucide-react";
import { IMAGES, CONTACT } from "@/data";

const lines = ["ACTION", "PACKED", "ADVENTURES"];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.35 } },
};
const lineVariant = {
  hidden: { y: "110%" },
  show: {
    y: "0%",
    transition: { type: "spring", stiffness: 90, damping: 18 },
  },
};

export const Hero = ({ onNavigate }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.05]);

  return (
    <section
      ref={ref}
      className="relative min-h-screen overflow-hidden bg-slate-50 pt-32 pb-16"
      data-testid="hero-section"
    >
      {/* background blobs */}
      <div className="pointer-events-none absolute -top-24 -left-24 h-[28rem] w-[28rem] rounded-full bg-sky-300/40 blur-3xl" />
      <div className="pointer-events-none absolute top-40 -right-20 h-[24rem] w-[24rem] rounded-full bg-sky-400/30 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 left-1/3 h-72 w-72 rounded-full bg-yellow-200/40 blur-3xl" />

      <div className="relative z-10 mx-auto max-w-7xl px-5 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="inline-flex items-center gap-2 rounded-full bg-sky-400/15 border border-sky-400/30 px-4 py-1.5 text-sm font-bold text-sky-700 mb-6"
          >
            <Sparkles className="h-4 w-4" /> Holiday Camps · Years 1 to 8
          </motion.div>

          <motion.h1
            variants={container}
            initial="hidden"
            animate="show"
            className="font-display font-black tracking-tighter leading-[0.85] text-slate-900 text-6xl sm:text-7xl lg:text-8xl"
          >
            {lines.map((line, i) => (
              <span key={line} className="reveal-mask">
                <motion.span
                  variants={lineVariant}
                  className={`block ${i === 1 ? "text-sky-500" : ""}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="mt-7 max-w-xl text-lg md:text-xl text-slate-600 font-medium"
          >
            Friendly multi-sport holiday camps for children — a relaxed,
            well-run day of sport, games and time spent with friends.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.96 }}
              onClick={() => onNavigate("#camps")}
              data-testid="hero-view-camps"
              className="inline-flex items-center gap-2 rounded-full bg-sky-400 px-8 py-4 font-display font-bold text-white text-lg shadow-[0_16px_40px_rgba(56,189,248,0.45)] hover:bg-sky-500 transition-colors"
            >
              View Camps & Prices
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.96 }}
              onClick={() => onNavigate("#contact")}
              data-testid="hero-get-in-touch"
              className="inline-flex items-center gap-2 rounded-full border-2 border-slate-900 px-8 py-4 font-display font-bold text-slate-900 text-lg hover:bg-slate-900 hover:text-white transition-colors"
            >
              Get in Touch
            </motion.button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="mt-7 inline-flex items-center gap-2 text-slate-500 font-semibold"
          >
            <MapPin className="h-5 w-5 text-sky-500" />
            {CONTACT.location}
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-[2.5rem] border-2 border-white shadow-[0_30px_70px_rgba(56,189,248,0.35)] aspect-[6/5]">
              <motion.img
                src={IMAGES.heroCheering}
                alt="Children cheering with hands up around the Action Packed Adventures logo"
                style={{ y: imgY, scale: imgScale }}
                className="h-full w-full object-cover object-center"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20, rotate: -6 }}
              animate={{ opacity: 1, y: 0, rotate: -6 }}
              transition={{ delay: 1.1 }}
              className="absolute -bottom-6 -left-6 rounded-3xl bg-yellow-400 px-6 py-4 shadow-xl"
            >
              <p className="font-display font-black text-2xl text-slate-900 leading-none">
                4 Days
              </p>
              <p className="font-bold text-slate-800 text-sm mt-1">
                of pure fun
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20, rotate: 5 }}
              animate={{ opacity: 1, y: 0, rotate: 5 }}
              transition={{ delay: 1.25 }}
              className="absolute -top-5 -right-3 rounded-2xl bg-white border-2 border-slate-900/10 px-5 py-3 shadow-xl"
            >
              <p className="font-display font-black text-xl text-sky-500 leading-none">
                From £40
              </p>
              <p className="font-bold text-slate-600 text-xs mt-1">
                early bird / day
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.button
        onClick={() => onNavigate("#about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        data-testid="hero-scroll-down"
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2 text-slate-400"
      >
        <span className="text-xs font-bold uppercase tracking-widest">
          Scroll
        </span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
        >
          <ArrowDown className="h-5 w-5" />
        </motion.span>
      </motion.button>
    </section>
  );
};

export default Hero;
